import { useTranslation } from 'react-i18next';
import { useOnlineStore } from '@/lib/online-store';
import { useAudioQueueStore } from '@/lib/audio-queue-store';
import { enhancedVoiceAllowed } from '@/lib/core-cutover';
import styles from './OfflineIndicator.module.css';

/**
 * Quiet, reverent offline indicator (offline-first Chunk 4, §5.3). A small,
 * dim, auto-hiding pill shown ONLY while offline, in the same top-center slot
 * the PendingTranscriptionPill occupies on PrayPage. No dismiss button: it
 * self-hides on reconnect.
 *
 * Mutually exclusive with the pending pill so the one top-center slot never
 * holds two pills: renders iff offline AND no audio is queued. When offline
 * with queued audio, the pending pill ("Finishing N prayers when online.")
 * already conveys offline, so this calm pill yields to it.
 *
 * Acts only on `!online` (the app-wide asymmetry: navigator.onLine is trusted
 * only as "definitely offline"), so a flaky-network false-positive can only
 * ever under-claim offline, never falsely tell the user they are offline.
 */
export default function OfflineIndicator() {
  const { t } = useTranslation();
  const online = useOnlineStore((s) => s.online);
  const pendingCount = useAudioQueueStore((s) => s.pendingCount);

  if (online || (enhancedVoiceAllowed() && pendingCount > 0)) return null;

  return (
    <div className={styles.pill} role="status" aria-live="polite">
      {t('pray.offlineIndicator')}
    </div>
  );
}
